import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AuthProvider }       from './context/AuthContext';
import { ThemeProvider }      from './context/ThemeContext';
import { CalendarProvider }   from './context/CalendarContext';
import { TenantProvider, useTenant } from './context/TenantContext';
import { SuperAuthProvider }  from './context/SuperAuthContext';
import ProtectedRoute         from './components/common/ProtectedRoute';
import ErrorBoundary          from './components/common/ErrorBoundary';
import AdminLayout            from './components/layout/AdminLayout';
import PortalLayout           from './components/layout/PortalLayout';
import SuperAdminLayout       from './layouts/SuperAdminLayout';
import ImpersonationBanner    from './components/common/ImpersonationBanner';
import SuspendedClub          from './pages/SuspendedClub';
import useAuth                from './hooks/useAuth';

import Login               from './pages/auth/Login';
import StudentSignup       from './pages/auth/StudentSignup';
import TrainerSignup       from './pages/auth/TrainerSignup';
import VerifyEmail         from './pages/auth/VerifyEmail';
import ForgotPassword      from './pages/auth/ForgotPassword';
import ResetPassword       from './pages/auth/ResetPassword';
import PendingApproval     from './pages/auth/PendingApproval';
import ChangePassword      from './pages/auth/ChangePassword';

import MarketingHome       from './pages/marketing/Home';
import ClubSignup          from './pages/marketing/ClubSignup';
import AcceptInvite        from './pages/invite/AcceptInvite';

import AdminExpenses       from './pages/admin/Expenses';
import AdminFinance        from './pages/admin/Finance';
import SupportAccessLog    from './pages/admin/SupportAccessLog';
import SalesLeads          from './pages/superadmin/SalesLeads';
import LaunchTesting       from './pages/admin/LaunchTesting';
import ActivityLog         from './pages/admin/ActivityLog';
import TrainerMyExpenses   from './pages/trainer/MyExpenses';
import AdminDashboard      from './pages/admin/Dashboard';
import AdminStudents       from './pages/admin/Students';
import AdminTrainers       from './pages/admin/Trainers';
import AdminClasses        from './pages/admin/Classes';
import AdminBeltExams      from './pages/admin/BeltExams';
import AdminGrading        from './pages/admin/Grading';
import AdminGradingBelt    from './pages/admin/GradingBelt';
import AdminAttendanceMessages from './pages/admin/AttendanceMessages';
import AdminAnnouncements  from './pages/admin/Announcements';
import AdminStudentRecords from './pages/admin/StudentRecords';
import AdminTrainingJournal from './pages/admin/TrainingJournal';
import AdminPayments       from './pages/admin/Payments';
import AdminReports        from './pages/admin/Reports';
import AdminSettings       from './pages/admin/Settings';
import AdminApprovals      from './pages/admin/Approvals';
import AdminAdmins         from './pages/admin/Admins';
import ClassDetail         from './pages/admin/ClassDetail';

import AdminSessions       from './pages/admin/Sessions';
import MySessions          from './pages/trainer/MySessions';
import AttendanceDay       from './pages/admin/AttendanceDay';

import TrainerDashboard    from './pages/trainer/TrainerDashboard';
import MyStudents          from './pages/trainer/MyStudents';
import TrainerSchedule     from './pages/trainer/MySchedule';
import BeltRecommendations from './pages/trainer/BeltRecommendations';
import TrainerBeltExams    from './pages/trainer/BeltExams';
import TrainerAnnouncements from './pages/trainer/TrainerAnnouncements';
import TrainerStudentRecords from './pages/trainer/StudentRecords';
import TrainerTrainingJournal from './pages/trainer/TrainingJournal';
import TrainerSettings     from './pages/trainer/Settings';

import StudentDashboard    from './pages/student/StudentDashboard';
import MyProfile           from './pages/student/MyProfile';
import MyAttendance        from './pages/student/MyAttendance';
import StudentSchedule     from './pages/student/MySchedule';
import BeltProgress        from './pages/student/BeltProgress';
import BeltGate            from './components/common/BeltGate';
import ClassGate           from './components/common/ClassGate';
import FeePayment          from './pages/student/FeePayment';
import Notices             from './pages/student/Notices';
import BadgeGallery        from './pages/student/BadgeGallery';
import StudentTrainingJournal from './pages/student/TrainingJournal';
import Leaderboard         from './pages/student/Leaderboard';
import StudentSettings     from './pages/student/Settings';

import SuperLogin              from './pages/superadmin/SuperLogin';
import SuperDashboard          from './pages/superadmin/SuperDashboard';
import ClubsList               from './pages/superadmin/ClubsList';
import ClubDetailSA            from './pages/superadmin/ClubDetail';
import AuditLogs               from './pages/superadmin/AuditLogs';
import ImpersonationPage       from './pages/superadmin/ImpersonationPage';
import Team                    from './pages/superadmin/Team';
import SASubscriptions         from './pages/superadmin/Subscriptions';
import SADiscounts             from './pages/superadmin/Discounts';
import SubscriptionPage        from './pages/subscription/SubscriptionPage';
import TrialApplication        from './pages/public/TrialApplication';
import TrialJoin               from './pages/public/TrialJoin';
import TrialStudents           from './pages/admin/TrialStudents';
import { TRIAL_ENABLED, APPROVALS_ENABLED } from './config/features';
import Marketing               from './pages/admin/Marketing';
import ClubProfile             from './pages/admin/ClubProfile';
import SelfSignup              from './pages/auth/SelfSignup';
import PublicClubProfile       from './pages/public/ClubProfile';
import ParentPayment           from './pages/public/ParentPayment';
import ClubInvite              from './pages/public/ClubInvite';
import DemoView                from './pages/public/DemoView';
import SADemoLinks             from './pages/superadmin/DemoLinks';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 1000 * 60 * 5 },
  },
});

function TenantGate({ children }) {
  const { error } = useTenant();
  const { token } = useAuth();
  const { pathname } = useLocation();

  // CLUB_SUSPENDED has a narrow recovery path: the admin must be able to reach /login to
  // attempt it, and an already-logged-in admin's session must keep working on a page refresh
  // (this public by-slug lookup re-runs on every load, before AuthProvider's own /me refresh
  // has a chance to render anything). Both cases are let through here; actual restriction to
  // just the subscription page happens downstream in AdminLayout — this is not the security
  // boundary either way, the backend (tenant.js + protect) enforces it regardless of what
  // the frontend renders. CLUB_CANCELLED has no recovery path for any state, unchanged.
  if (error === 'CLUB_SUSPENDED' && (pathname === '/login' || !!token)) return children;
  if (error === 'CLUB_SUSPENDED' || error === 'CLUB_CANCELLED') return <SuspendedClub />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <SuperAuthProvider>
        <TenantProvider>
          <AuthProvider>
            <ThemeProvider>
            <CalendarProvider>
              <BrowserRouter>
                <Routes>
                  {/* ── Super Admin (outside TenantGate) ── */}
                  <Route path="/superadmin" element={<SuperLogin />} />
                  <Route path="/superadmin/*" element={<SuperAdminLayout />}>
                    <Route path="dashboard"     element={<SuperDashboard />} />
                    <Route path="clubs"         element={<ClubsList />} />
                    <Route path="clubs/:id"     element={<ClubDetailSA />} />
                    <Route path="audit-logs"    element={<AuditLogs />} />
                    <Route path="impersonation" element={<ImpersonationPage />} />
                    <Route path="team"          element={<Team />} />
                    <Route path="subscriptions" element={<SASubscriptions />} />
                    <Route path="discounts"     element={<SADiscounts />} />
                    <Route path="leads"         element={<SalesLeads />} />
                    <Route path="demo-links"    element={<SADemoLinks />} />
                  </Route>

                  {/* ── Parent payment link — no tenant, no auth required ── */}
                  <Route path="/pay/:token" element={<ParentPayment />} />

                  {/* ── Club-owner invitation accept — no tenant, no auth required ── */}
                  <Route path="/club-invite/:token" element={<ClubInvite />} />

                  {/* ── Public read-only demo — no tenant, no auth required ── */}
                  <Route path="/demo/:slug" element={<DemoView />} />

                  {/* ── All other routes (inside TenantGate) ── */}
                  <Route path="*" element={
                    <TenantGate>
                      <ImpersonationBanner />
                      <Toaster position="top-right" toastOptions={{ duration: 3000, style: { fontSize: '0.875rem' } }} />
                      <Routes>
                        {/* Public club profile — no auth required */}
                        <Route path="/:clubSlug" element={<PublicClubProfile />} />
                        {/* Public trial registration — no auth required (hidden behind TRIAL_ENABLED) */}
                        {TRIAL_ENABLED && <Route path="/:clubSlug/trial" element={<TrialApplication />} />}
                        {/* Public member self-signup — no auth required (hidden when invite-only) */}
                        {APPROVALS_ENABLED && <Route path="/:clubSlug/join" element={<SelfSignup />} />}
                        {/* Public trial-to-member join — no auth required */}
                        <Route path="/join/:token" element={<TrialJoin />} />

                        <Route path="/"              element={<MarketingHome />} />
                        <Route path="/signup"        element={<ClubSignup />} />
                        <Route path="/accept-invite/:token" element={<AcceptInvite />} />

                        <Route path="/login"                  element={<Login />} />
                        <Route path="/signup/student"         element={<StudentSignup />} />
                        <Route path="/signup/trainer"         element={<TrainerSignup />} />
                        <Route path="/verify-email/:token"    element={<VerifyEmail />} />
                        <Route path="/forgot-password"        element={<ForgotPassword />} />
                        <Route path="/reset-password/:token"  element={<ResetPassword />} />
                        <Route path="/pending-approval"       element={<PendingApproval />} />
                        <Route path="/change-password"        element={<ChangePassword />} />

                        <Route path="/admin" element={
                          <ProtectedRoute allowedRoles={['admin']}>
                            <AdminLayout />
                          </ProtectedRoute>
                        }>
                          <Route index element={<Navigate to="dashboard" replace />} />
                          <Route path="dashboard"        element={<AdminDashboard />} />
                          <Route path="students"         element={<AdminStudents />} />
                          <Route path="trainers"         element={<AdminTrainers />} />
                          <Route path="classes"          element={<ClassGate redirectTo="/admin/dashboard"><AdminClasses /></ClassGate>} />
                          <Route path="belt-exams"       element={<BeltGate redirectTo="/admin/dashboard"><AdminBeltExams /></BeltGate>} />
                          <Route path="grading"          element={<BeltGate redirectTo="/admin/dashboard"><AdminGrading /></BeltGate>} />
                          <Route path="grading/:belt"    element={<BeltGate redirectTo="/admin/dashboard"><AdminGradingBelt /></BeltGate>} />
                          <Route path="attendance-messages" element={<AdminAttendanceMessages />} />
                          <Route path="announcements"    element={<AdminAnnouncements />} />
                          <Route path="student-records"  element={<AdminStudentRecords />} />
                          <Route path="training-journal" element={<ClassGate redirectTo="/admin/dashboard"><AdminTrainingJournal /></ClassGate>} />
                          <Route path="payments"         element={<AdminPayments />} />
                          <Route path="reports"          element={<AdminReports />} />
                          <Route path="settings"         element={<AdminSettings />} />
                          {APPROVALS_ENABLED && <Route path="approvals"        element={<AdminApprovals />} />}
                          <Route path="admins"           element={<AdminAdmins />} />
                          <Route path="subscription"     element={<SubscriptionPage />} />
                          <Route path="classes/:classId" element={<ClassGate redirectTo="/admin/dashboard"><ClassDetail /></ClassGate>} />
                          {/* Simple Attendance (A2) — takes the nav slot; /admin/sessions stays alive below */}
                          <Route path="attendance"       element={<ClassGate redirectTo="/admin/dashboard"><AttendanceDay /></ClassGate>} />
                          <Route path="sessions"         element={<ClassGate redirectTo="/admin/dashboard"><AdminSessions /></ClassGate>} />
                          {TRIAL_ENABLED && <Route path="trial-students"   element={<TrialStudents />} />}
                          <Route path="marketing"        element={<Marketing />} />
                          <Route path="club-profile"     element={<ClubProfile />} />
                          <Route path="expenses"         element={<AdminExpenses />} />
                          <Route path="finance"          element={<AdminFinance />} />
                          <Route path="support-access"   element={<SupportAccessLog />} />
                          <Route path="launch-testing"   element={<LaunchTesting />} />
                          <Route path="activity"         element={<ActivityLog />} />
                        </Route>

                        <Route path="/trainer" element={
                          <ProtectedRoute allowedRoles={['trainer']}>
                            <PortalLayout />
                          </ProtectedRoute>
                        }>
                          <Route index element={<Navigate to="dashboard" replace />} />
                          <Route path="dashboard"            element={<TrainerDashboard />} />
                          <Route path="students"             element={<MyStudents />} />
                          {/* Simple Attendance (A2) — replaces the orphaned legacy AttendanceMarking here; that file is kept, just no longer routed. /trainer/sessions stays alive below. */}
                          <Route path="attendance"           element={<ClassGate redirectTo="/trainer/dashboard"><AttendanceDay trainerOnly /></ClassGate>} />
                          <Route path="schedule"             element={<ClassGate redirectTo="/trainer/dashboard"><TrainerSchedule /></ClassGate>} />
                          <Route path="belt-recommendations" element={<BeltGate redirectTo="/trainer/dashboard"><BeltRecommendations /></BeltGate>} />
                          <Route path="belt-exams"           element={<BeltGate redirectTo="/trainer/dashboard"><TrainerBeltExams /></BeltGate>} />
                          <Route path="announcements"        element={<TrainerAnnouncements />} />
                          <Route path="student-records"      element={<TrainerStudentRecords />} />
                          <Route path="training-journal"     element={<ClassGate redirectTo="/trainer/dashboard"><TrainerTrainingJournal /></ClassGate>} />
                          <Route path="expenses"             element={<TrainerMyExpenses />} />
                          <Route path="settings"             element={<TrainerSettings />} />
                          <Route path="sessions"             element={<ClassGate redirectTo="/trainer/dashboard"><MySessions /></ClassGate>} />
                          {TRIAL_ENABLED && <Route path="trial-students"       element={<TrialStudents />} />}
                        </Route>

                        <Route path="/student" element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <PortalLayout />
                          </ProtectedRoute>
                        }>
                          <Route index element={<Navigate to="dashboard" replace />} />
                          <Route path="dashboard"     element={<StudentDashboard />} />
                          <Route path="profile"       element={<MyProfile />} />
                          <Route path="attendance"    element={<ClassGate redirectTo="/student/dashboard"><MyAttendance /></ClassGate>} />
                          <Route path="schedule"      element={<ClassGate redirectTo="/student/dashboard"><StudentSchedule /></ClassGate>} />
                          <Route path="belt-progress" element={<BeltGate redirectTo="/student/dashboard"><BeltProgress /></BeltGate>} />
                          <Route path="fees"          element={<FeePayment />} />
                          <Route path="notices"       element={<Notices />} />
                          <Route path="badges"        element={<BadgeGallery />} />
                          <Route path="training-journal" element={<ClassGate redirectTo="/student/dashboard"><StudentTrainingJournal /></ClassGate>} />
                          <Route path="leaderboard"   element={<Leaderboard />} />
                          <Route path="settings"         element={<StudentSettings />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/login" replace />} />
                      </Routes>
                    </TenantGate>
                  } />
                </Routes>
              </BrowserRouter>
            </CalendarProvider>
            </ThemeProvider>
          </AuthProvider>
        </TenantProvider>
      </SuperAuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;